import { useEffect, useState } from 'react';
import { categoriasService } from '../services/api';
import { Plus, Pencil, Trash2, X } from 'lucide-react';

const COLORES = ['var(--brand)','#EA580C','#D97706','#16A34A','#0891B2','#2563EB','#7C3AED','#DB2777','#374151','#15803D'];
const formInicial = { nombre: '', color: 'var(--brand-texto)', subgrupo: '' };

const S = {
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' } as React.CSSProperties,
  th: { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' as const, letterSpacing: '.06em', textAlign: 'left' as const },
  td: { padding: '12px 16px', fontSize: 13, color: 'var(--ink-2)', borderTop: '1px solid var(--line-2)' } as React.CSSProperties,
};

export default function Categorias() {
  const [categorias, setCategorias] = useState<any[]>([]);
  const [form, setForm] = useState(formInicial);
  const [editando, setEditando] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => { const r = await categoriasService.getAll(); setCategorias(r.data); };
  useEffect(() => { cargar(); }, []);

  const abrir = (c?: any) => {
    if (c) { setEditando(c); setForm({ nombre: c.nombre, color: c.color || 'var(--brand-texto)', subgrupo: c.subgrupo || '' }); }
    else { setEditando(null); setForm(formInicial); }
    setError(''); setOpen(true);
  };
  const cerrar = () => setOpen(false);

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('El nombre es requerido'); return; }
    setGuardando(true); setError('');
    try {
      editando ? await categoriasService.actualizar(editando.id, form) : await categoriasService.crear(form);
      await cargar(); cerrar();
    } catch (e: any) { setError(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id: string) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try { await categoriasService.eliminar(id); await cargar(); }
    catch (e: any) { alert(e.response?.data?.error || 'Error'); }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', margin: 0 }}>Categorías</h1>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '2px 0 0' }}>{categorias.length} categoría{categorias.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => abrir()} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} /> Nueva categoría
        </button>
      </div>

      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg)' }}>
            <tr>
              <th style={S.th}>Categoría</th>
              <th style={S.th}>Subgrupo</th>
              <th style={{ ...S.th, textAlign: 'center' }}>Orden</th>
              <th style={{ ...S.th, width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {categorias.length === 0 ? (
              <tr><td colSpan={4} style={{ ...S.td, color: 'var(--faint)', textAlign: 'center', padding: '40px 16px' }}>Sin categorías</td></tr>
            ) : categorias.map(c => (
              <tr key={c.id}>
                <td style={S.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: c.color || 'var(--brand)', flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ fontWeight: 500 }}>{c.nombre}</span>
                  </div>
                </td>
                <td style={{ ...S.td, color: 'var(--faint)' }}>{c.subgrupo || '—'}</td>
                <td style={{ ...S.td, textAlign: 'center', color: 'var(--faint)' }}>{c.orden}</td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button onClick={() => abrir(c)} style={{ width: 30, height: 30, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}><Pencil size={13} /></button>
                    <button onClick={() => eliminar(c.id)} style={{ width: 30, height: 30, border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--danger)' }}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,.3)' }} onClick={cerrar} />
          <div style={{ width: 400, background: 'var(--surface)', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', height: 56, borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{editando ? 'Editar categoría' : 'Nueva categoría'}</span>
              <button onClick={cerrar} style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--brand-texto)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--danger-line)' }}>{error}</div>}
              {[
                { label: 'Nombre *', key: 'nombre', placeholder: 'Ej. Bebidas calientes' },
                { label: 'Subgrupo (opcional)', key: 'subgrupo', placeholder: 'Ej. Desayunos' },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input value={(form as any)[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                    style={{ width: '100%', height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-texto)')} onBlur={e => (e.target.style.borderColor = 'var(--line-strong)')} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 10 }}>Color</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {COLORES.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: form.color === c ? '3px solid var(--strong)' : '3px solid transparent', cursor: 'pointer', transition: 'transform .1s', transform: form.color === c ? 'scale(1.15)' : 'scale(1)' }} />
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, flexShrink: 0 }}>
              <button onClick={cerrar} style={{ flex: 1, height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, background: 'var(--surface)', fontSize: 13, cursor: 'pointer', color: 'var(--ink-2)' }}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{ flex: 2, height: 38, border: 'none', borderRadius: 8, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 13, fontWeight: 600, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .7 : 1 }}>
                {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear categoría'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
